import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ALL_COUNTRIES } from "@/data/countries";
import PhoneInput from "../PhoneInput";
import { CountrySelector } from "../CountrySelector";
import EmailAutocomplete from "../EmailAutocomplete";
import { CitySelector } from "../CitySelector";
import { FormSectionProps } from "./types";

const PersonalInformationSection: React.FC<FormSectionProps> = ({
  register,
  setValue,
  watchedFields,
  errors,
}) => {
  const [emailBlurred, setEmailBlurred] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-lg">Personal Information</h3>
      <div className="space-y-4">
        <div className="space-y-2" data-question-field="fullName">
          <Label htmlFor="fullName">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="fullName"
            {...register("fullName")}
            placeholder="Enter your first and last name"
            className={errors.fullName ? "border-red-500" : ""}
          />
          {errors.fullName && (
            <p className="text-sm text-red-500">{errors.fullName.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="email">
          <Label htmlFor="email">
            Email Address <span className="text-red-500">*</span>
          </Label>
          <EmailAutocomplete
            id="email"
            {...(() => {
              const { onBlur: registerOnBlur, ...registerProps } = register("email", {
                validate: (value) => {
                  // Only validate after user has blurred the field
                  if (!emailBlurred) {
                    return true; // Don't validate while typing
                  }
                  
                  if (!value || value.trim() === "") {
                    return "Email is required";
                  }
                  
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(value)) {
                    return "Invalid email address";
                  }
                  
                  return true;
                },
              });
              
              return {
                ...registerProps,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  setEmailBlurred(true);
                  registerOnBlur(e);
                },
              };
            })()}
            placeholder="youremail@gmail.com"
            error={!!errors.email && emailBlurred}
          />
          {errors.email && emailBlurred && (
            <p className="text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="phoneNumber">
          <Label>
            Phone number <span className="text-red-500">*</span>
          </Label>
          <PhoneInput
            countryCode={watchedFields.phoneCountryCode || "+33"}
            phoneNumber={watchedFields.phoneNumber || ""}
            onCountryCodeChange={(value) => setValue("phoneCountryCode", value)}
            onPhoneNumberChange={(value) => setValue("phoneNumber", value)}
            error={errors.phoneNumber?.message || errors.phoneCountryCode?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2" data-question-field="country">
            <Label htmlFor="country">
              Country <span className="text-red-500">*</span>
            </Label>
            <CountrySelector
              value={watchedFields.country || ""}
              onChange={(value) => setValue("country", value)}
              countryList={ALL_COUNTRIES}
              error={!!errors.country}
            />
            {errors.country && (
              <p className="text-sm text-red-500">{errors.country.message}</p>
            )}
          </div>

          <div className="space-y-2" data-question-field="city">
            <Label htmlFor="city">
              City <span className="text-red-500">*</span>
            </Label>
            <CitySelector
              value={watchedFields.city || ""}
              onChange={(value) => setValue("city", value)}
              countryName={
                watchedFields.country
                  ? ALL_COUNTRIES.find((c) => c.id === watchedFields.country)?.label || null
                  : null
              }
              error={!!errors.city}
            />
            {errors.city && (
              <p className="text-sm text-red-500">{errors.city.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-4" data-question-field="openToRoles">
          <Label>
            Are you open to new roles? <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={watchedFields.openToRoles}
            onValueChange={(value) => setValue("openToRoles", value as "yes" | "maybe" | "no")}
            className="space-y-1 text-neutral-500"
          >
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="yes" id="open-yes" />
              <Label htmlFor="open-yes" className="font-normal cursor-pointer">
                Yes, actively looking
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="maybe" id="open-maybe" />
              <Label htmlFor="open-maybe" className="font-normal cursor-pointer">
                Maybe, depends on the opportunity
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="no" id="open-no" />
              <Label htmlFor="open-no" className="font-normal cursor-pointer">
                No, not looking right now
              </Label>
            </div>
          </RadioGroup>
          {errors.openToRoles && (
            <p className="text-sm text-red-500">{errors.openToRoles.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInformationSection;

